import { Suspense } from 'react';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../../components/Navbar';
import Footer from '../../../home/components/Footer';

export default function SocketSwitchPage() {
  useEffect(() => {
    document.title = 'Socket & Switch Installation Watford | Beyond Electrical';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional socket and switch installation in Watford. Additional sockets, USB outlets, and dimmer switches. Call 020 3198 1818 for a free quote.');
    }

    const schema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Beyond Electrical Services",
      "image": "https://beyondelectrical.co.uk/logo.png",
      "telephone": "020 3198 1818",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Watford",
        "addressLocality": "Watford",
        "addressRegion": "Hertfordshire",
        "postalCode": "WD17",
        "addressCountry": "GB"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 51.6565,
        "longitude": -0.3957
      },
      "url": "https://beyondelectrical.co.uk/services/installations/socket-switch",
      "areaServed": "Watford",
      "serviceType": "Socket & Switch Installation",
      "openingHours": "Mo-Su 00:00-23:59"
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 bg-gradient-to-br from-amber-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm mb-6">
                <i className="ri-map-pin-line text-amber-500"></i>
                <span className="text-sm font-medium text-gray-700">Serving Watford & Surrounding Areas</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                Socket & Switch Installation <span className="text-amber-500">in Watford</span>
              </h1>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Professional socket and switch installation services. From single outlets to complete room upgrades, we provide safe, compliant installations throughout Watford and Rickmansworth.
              </p>
              
              <div className="flex flex-wrap gap-4 justify-center mb-8">
                <a href="tel:02031981818" className="inline-flex items-center gap-2 bg-amber-500 text-white px-8 py-4 rounded-lg font-semibold hover:bg-amber-600 transition-colors shadow-lg whitespace-nowrap cursor-pointer">
                  <i className="ri-phone-line text-xl"></i>
                  Call 020 3198 1818
                </a>
                <button onClick={scrollToContact} className="inline-flex items-center gap-2 bg-gray-900 text-white px-8 py-4 rounded-lg font-semibold hover:bg-gray-800 transition-colors shadow-lg whitespace-nowrap cursor-pointer">
                  <i className="ri-mail-line text-xl"></i>
                  Get Free Quote
                </button>
              </div>
              
              <div className="flex flex-wrap gap-6 justify-center text-sm">
                <div className="flex items-center gap-2">
                  <i className="ri-shield-check-line text-amber-500 text-xl"></i>
                  <span className="text-gray-700 font-medium">NICEIC Certified</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-time-line text-amber-500 text-xl"></i>
                  <span className="text-gray-700 font-medium">15 Years Experience</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-checkbox-circle-line text-amber-500 text-xl"></i>
                  <span className="text-gray-700 font-medium">Fully Insured</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Included */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Socket & Switch Services</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Complete installation and upgrade services for all types of sockets and switches
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-xl border border-amber-100">
                <div className="w-14 h-14 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-plug-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Additional Sockets</h3>
                <p className="text-gray-600 leading-relaxed">
                  Install extra power outlets where you need them most. Single, double, or USB sockets available.
                </p>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-xl border border-amber-100">
                <div className="w-14 h-14 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-toggle-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Light Switch Upgrades</h3>
                <p className="text-gray-600 leading-relaxed">
                  Replace old switches with modern dimmer switches, smart switches, or designer finishes.
                </p>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-xl border border-amber-100">
                <div className="w-14 h-14 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-usb-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">USB Socket Installation</h3>
                <p className="text-gray-600 leading-relaxed">
                  Modern USB charging sockets with fast-charge capability for phones and tablets.
                </p>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-xl border border-amber-100">
                <div className="w-14 h-14 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-water-flash-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Outdoor Sockets</h3>
                <p className="text-gray-600 leading-relaxed">
                  Weatherproof outdoor power points for garden equipment, lighting, and entertainment.
                </p>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-xl border border-amber-100">
                <div className="w-14 h-14 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-home-gear-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Cooker & Appliance Points</h3>
                <p className="text-gray-600 leading-relaxed">
                  Dedicated high-power outlets for cookers, ovens, and large kitchen appliances.
                </p>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-xl border border-amber-100">
                <div className="w-14 h-14 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-palette-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Designer Finishes</h3>
                <p className="text-gray-600 leading-relaxed">
                  Chrome, brass, black nickel, or white finishes to match your interior design.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Upgrade Your Sockets & Switches?</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Modern sockets and switches offer convenience, safety, and style
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center">
                    <i className="ri-flashlight-line text-xl text-white"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">More Power Points</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Eliminate extension leads and power strips with properly positioned outlets throughout your home.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center">
                    <i className="ri-smartphone-line text-xl text-white"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Built-in USB Charging</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Charge devices directly without bulky adapters. Fast-charge USB-C options available.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center">
                    <i className="ri-shield-check-line text-xl text-white"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Enhanced Safety</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Modern sockets with surge protection and child safety features for peace of mind.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center">
                    <i className="ri-paint-brush-line text-xl text-white"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Stylish Designs</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Premium finishes and modern designs that complement your home's interior style.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Beyond Electrical?</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Trusted socket and switch installation experts in Watford
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-20 h-20 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-award-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">15 Years Experience</h3>
                <p className="text-gray-600 leading-relaxed">
                  Over a decade of professional electrical installations across Watford and Hertfordshire.
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-shield-star-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">NICEIC Certified</h3>
                <p className="text-gray-600 leading-relaxed">
                  All work certified and compliant with BS 7671 18th Edition wiring regulations.
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-map-pin-user-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Local to Watford</h3>
                <p className="text-gray-600 leading-relaxed">
                  Based in Watford, we provide fast, reliable service throughout the local area.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Areas Covered */}
        <section className="py-16 bg-gradient-to-br from-amber-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Areas We Cover</h2>
              <div className="flex flex-wrap justify-center gap-4 text-gray-700">
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Watford</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Rickmansworth</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Bushey</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Abbots Langley</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Croxley Green</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Chorleywood</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Northwood</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Radlett</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Borehamwood</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">St Albans</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Hemel Hempstead</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm font-medium">Harrow</span>
              </div>
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section id="contact" className="py-20 bg-gray-900 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold mb-6">Ready to Upgrade Your Sockets & Switches?</h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Get a free, no-obligation quote for your socket and switch installation in Watford
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a href="tel:02031981818" className="inline-flex items-center gap-2 bg-amber-500 text-white px-8 py-4 rounded-lg font-semibold hover:bg-amber-600 transition-colors shadow-lg whitespace-nowrap cursor-pointer">
                <i className="ri-phone-line text-xl"></i>
                Call 020 3198 1818
              </a>
              <a href="mailto:info@beyondelectrical.co.uk" className="inline-flex items-center gap-2 bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg whitespace-nowrap cursor-pointer">
                <i className="ri-mail-line text-xl"></i>
                Email Us
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </Suspense>
  );
}